import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { catchError, finalize, of } from 'rxjs';
import { WnPerms } from '../../../../core/auth/worknest-admin-permissions';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../core/i18n/translation.service';
import type { AssetCharge, AssetChargeReason, AssetChargeStatus } from '../../../../core/models/assets/asset-charges.dto';
import { AssetChargesService } from '../../../../core/services/asset-charges.service';
import { PermissionService } from '../../../../core/services/permission.service';
import { ToastFeedbackService } from '../../../../core/services/toast-feedback.service';
import { EmptyState } from '../../../../shared/components/empty-state/empty-state';
import { ListPaginatorComponent } from '../../../../shared/components/list-paginator/list-paginator.component';
import { StatusChip } from '../../../../shared/components/status-chip/status-chip';
import { UserLookupSelectComponent } from '../../../../shared/components/user-lookup-select/user-lookup-select.component';
import { chargeAssetLabel } from './asset-charge-labels';
import { PurchaseChargeDialogComponent } from './purchase-charge-dialog/purchase-charge-dialog.component';

/** The tenant-wide list of asset charges (lost, damaged and purchase), with filters and the new purchase charge action. */
@Component({
  selector: 'app-asset-charges-page',
  standalone: true,
  imports: [RouterLink, TranslatePipe, ButtonModule, EmptyState, ListPaginatorComponent, StatusChip, UserLookupSelectComponent, PurchaseChargeDialogComponent],
  templateUrl: './asset-charges-page.component.html',
  styleUrl: './asset-charges-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetChargesPageComponent {
  private readonly charges = inject(AssetChargesService);
  private readonly permissions = inject(PermissionService);
  private readonly translation = inject(Translation);
  private readonly toast = inject(ToastFeedbackService);

  protected readonly reasons: AssetChargeReason[] = [1, 2, 3];
  protected readonly statuses: AssetChargeStatus[] = [1, 2, 3];
  protected readonly canManage = computed(() => this.permissions.hasAny(WnPerms.assets.chargeManage));
  protected readonly rows = signal<AssetCharge[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly skip = signal(0);
  protected readonly pageSize = signal(20);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly reason = signal('');
  protected readonly status = signal('');
  protected readonly userId = signal('');
  protected readonly purchaseOpen = signal(false);
  private readonly isArabic = computed(() => this.translation.getCurrentLang().toLowerCase().startsWith('ar'));

  constructor() {
    this.reload();
  }

  protected assetLabel(charge: AssetCharge): string {
    return chargeAssetLabel(charge, this.isArabic());
  }

  protected setReason(value: string): void {
    this.reason.set(value);
    this.applyFilter();
  }

  protected setStatus(value: string): void {
    this.status.set(value);
    this.applyFilter();
  }

  protected setUser(value: string): void {
    this.userId.set(value);
    this.applyFilter();
  }

  protected resetFilters(): void {
    this.reason.set('');
    this.status.set('');
    this.userId.set('');
    this.applyFilter();
  }

  protected changePage(next: number): void {
    const safe = Math.max(0, Math.trunc(next || 0));
    if (safe === this.skip()) return;
    this.skip.set(safe);
    this.reload();
  }

  protected changePageSize(next: number): void {
    const safe = Math.max(1, Math.trunc(next || 0));
    if (safe === this.pageSize()) return;
    this.pageSize.set(safe);
    this.skip.set(0);
    this.reload();
  }

  protected onPurchaseCreated(): void {
    this.purchaseOpen.set(false);
    this.toast.successKey('ASSET_CHARGES.PURCHASE_DONE');
    this.reload();
  }

  private applyFilter(): void {
    this.skip.set(0);
    this.reload();
  }

  protected reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.charges
      .list({
        userId: this.userId() || undefined,
        reason: this.reason() ? (Number(this.reason()) as AssetChargeReason) : undefined,
        status: this.status() ? (Number(this.status()) as AssetChargeStatus) : undefined,
        pageNumber: Math.floor(this.skip() / this.pageSize()) + 1,
        pageSize: this.pageSize(),
      })
      .pipe(
        catchError((err) => {
          this.error.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
          return of({ items: [], totalCount: 0 });
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((page) => {
        this.rows.set(page.items ?? []);
        this.totalCount.set(page.totalCount ?? 0);
      });
  }
}
